/**
 * Auto-Print Station
 * 
 * Diese Seite läuft im Hintergrund auf dem Restaurant-PC und druckt
 * automatisch neue Bestellungen, sobald sie eingehen.
 * 
 * Nutzung:
 * 1. Diese Seite auf dem PC im Restaurant öffnen
 * 2. QZ Tray muss laufen
 * 3. Drucker einmalig zuweisen
 * 4. Seite offen lassen - druckt automatisch!
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Printer, Wifi, WifiOff, Volume2, VolumeX, Bell, CheckCircle,
  AlertTriangle, RefreshCw, Settings, Clock, Loader2, Power
} from 'lucide-react';
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import useQZTray from '@/hooks/useQZTray';

// Sound für neue Bestellungen
const playNotificationSound = () => {
  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);
    
    oscillator.frequency.value = 800;
    oscillator.type = 'sine';
    gainNode.gain.value = 0.3;
    
    oscillator.start();
    setTimeout(() => {
      oscillator.frequency.value = 1000;
    }, 150);
    setTimeout(() => {
      oscillator.stop();
    }, 300);
  } catch (e) {
    console.log('Audio nicht verfügbar');
  }
};

export default function AutoPrint() {
  const [sessionToken, setSessionToken] = useState(null);
  const [isActive, setIsActive] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [pollInterval, setPollInterval] = useState(5); // Sekunden
  const [lastCheck, setLastCheck] = useState(null);
  const [printedOrders, setPrintedOrders] = useState(new Set());
  const [recentPrints, setRecentPrints] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [printerConfig, setPrinterConfig] = useState({
    customer: '',
    kitchen: '',
    bar: ''
  });
  const [configMode, setConfigMode] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  
  const pollTimerRef = useRef(null);
  const printedOrdersRef = useRef(new Set());
  
  // QZ Tray
  const {
    isConnected,
    isConnecting,
    qzLoaded,
    printers,
    connect,
    printThermalReceipt
  } = useQZTray();

  // Token laden
  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (token) {
      setSessionToken(token);
    }
    
    // Gespeicherte Konfiguration laden
    const savedConfig = localStorage.getItem('autoprint-printer-config');
    if (savedConfig) {
      try {
        setPrinterConfig(JSON.parse(savedConfig));
      } catch (e) {}
    }
    
    // Gedruckte Bestellungen aus Session laden
    const savedPrinted = sessionStorage.getItem('autoprint-printed-orders');
    if (savedPrinted) {
      try {
        const parsed = JSON.parse(savedPrinted);
        setPrintedOrders(new Set(parsed));
        printedOrdersRef.current = new Set(parsed);
      } catch (e) {}
    }
  }, []);

  // Auto-Connect
  useEffect(() => {
    if (qzLoaded && !isConnected && !isConnecting) {
      connect();
    }
  }, [qzLoaded, isConnected, isConnecting, connect]);

  // Drucker speichern
  const savePrinterConfig = (role, printer) => {
    const newConfig = { ...printerConfig, [role]: printer };
    setPrinterConfig(newConfig);
    localStorage.setItem('autoprint-printer-config', JSON.stringify(newConfig));
  };

  // Bestellung drucken
  const printOrder = useCallback(async (order, type = 'customer') => {
    const printerName = printerConfig[type];
    if (!printerName) return false;
    
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(
        `${backendUrl}/api/qz-tray/receipt/${order.transaction_id}?receipt_type=${type}`,
        { headers: { 'Authorization': `Bearer ${sessionToken}` } }
      );
      
      if (!response.ok) return false;
      
      const data = await response.json();
      await printThermalReceipt(data.html, printerName);
      return true;
    } catch (err) {
      console.error('Druckfehler:', err);
      return false;
    }
  }, [printerConfig, sessionToken, printThermalReceipt]);

  // Neue Bestellungen prüfen und drucken
  const checkAndPrint = useCallback(async () => {
    if (!sessionToken || !isConnected || isPrinting) return;
    
    setLastCheck(new Date());
    
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      
      // Hole neue Transaktionen der letzten 30 Minuten
      const thirtyMinAgo = new Date(Date.now() - 30 * 60 * 1000).toISOString();
      const response = await fetch(
        `${backendUrl}/api/transactions?limit=20&date_from=${thirtyMinAgo}`,
        { headers: { 'Authorization': `Bearer ${sessionToken}` } }
      );
      
      if (!response.ok) return;
      
      const data = await response.json();
      const transactions = data.transactions || [];
      
      // Filtere bereits gedruckte
      const newOrders = transactions.filter(t => 
        !printedOrdersRef.current.has(t.transaction_id)
      );
      
      setPendingCount(newOrders.length);
      
      if (newOrders.length > 0) {
        setIsPrinting(true);
        
        for (const order of newOrders) {
          // Sound abspielen
          if (soundEnabled) {
            playNotificationSound();
          }
          
          // Kundenbon drucken
          if (printerConfig.customer) {
            await printOrder(order, 'customer');
          }
          
          // Küchenbon drucken (wenn Drucker konfiguriert)
          if (printerConfig.kitchen) {
            await printOrder(order, 'kitchen');
          }
          
          // Als gedruckt markieren
          printedOrdersRef.current.add(order.transaction_id);
          setPrintedOrders(new Set(printedOrdersRef.current));
          sessionStorage.setItem('autoprint-printed-orders', 
            JSON.stringify([...printedOrdersRef.current]));
          
          // Zu Recent Prints hinzufügen
          setRecentPrints(prev => [{
            id: order.transaction_id,
            receipt_no: order.receipt_no,
            total: order.total_gross,
            time: new Date().toLocaleTimeString('de-DE')
          }, ...prev.slice(0, 9)]);
          
          toast.success(`${order.receipt_no} gedruckt!`);
        }
        
        setIsPrinting(false);
        setPendingCount(0);
      }
    } catch (err) {
      console.error('Fehler beim Prüfen:', err);
    }
  }, [sessionToken, isConnected, isPrinting, soundEnabled, printerConfig, printOrder]);

  // Polling starten/stoppen
  useEffect(() => {
    if (isActive && isConnected && printerConfig.customer) {
      // Sofort prüfen
      checkAndPrint();
      
      // Polling starten
      pollTimerRef.current = setInterval(checkAndPrint, pollInterval * 1000);
      
      return () => {
        if (pollTimerRef.current) {
          clearInterval(pollTimerRef.current);
        }
      };
    }
  }, [isActive, isConnected, pollInterval, printerConfig.customer, checkAndPrint]);

  // Login erforderlich
  if (!sessionToken) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <Card className="bg-gray-800 border-gray-700 max-w-md">
          <CardContent className="pt-6 text-center">
            <AlertTriangle className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-white mb-2">Anmeldung erforderlich</h2>
            <p className="text-gray-400 mb-4">
              Bitte melden Sie sich an, um Auto-Print zu nutzen.
            </p>
            <Button 
              onClick={() => window.location.href = '/login'}
              className="bg-teal-600 hover:bg-teal-700"
            >
              Zur Anmeldung
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white p-4" data-testid="auto-print-page">
      <div className="max-w-2xl mx-auto space-y-4">
        
        {/* Header */}
        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold flex items-center justify-center gap-3">
            <Printer className="w-8 h-8 text-teal-500" />
            Auto-Print Station
          </h1>
          <p className="text-gray-400 mt-1">
            Druckt neue Bestellungen automatisch
          </p>
        </div>

        {/* Status Card */}
        <Card className={`border-2 transition-colors ${
          isActive && isConnected 
            ? 'bg-teal-900/30 border-teal-500' 
            : 'bg-gray-800 border-gray-700'
        }`}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className={`w-16 h-16 rounded-full flex items-center justify-center ${
                  isActive && isConnected 
                    ? 'bg-teal-500 animate-pulse' 
                    : 'bg-gray-700'
                }`}>
                  <Power className={`w-8 h-8 ${
                    isActive && isConnected ? 'text-white' : 'text-gray-500'
                  }`} />
                </div>
                <div>
                  <div className="text-xl font-bold">
                    {isActive && isConnected ? 'AKTIV' : 'INAKTIV'}
                  </div>
                  <div className="text-sm text-gray-400">
                    {!isConnected 
                      ? 'QZ Tray nicht verbunden' 
                      : !printerConfig.customer
                        ? 'Kein Drucker konfiguriert'
                        : isActive 
                          ? `Prüft alle ${pollInterval}s`
                          : 'Bereit zum Starten'
                    }
                  </div>
                </div>
              </div>
              
              <Button
                size="lg"
                onClick={() => setIsActive(!isActive)}
                disabled={!isConnected || !printerConfig.customer}
                className={isActive 
                  ? 'bg-red-600 hover:bg-red-700' 
                  : 'bg-teal-600 hover:bg-teal-700'
                }
                data-testid="toggle-autoprint"
              >
                {isActive ? 'Stoppen' : 'Starten'}
              </Button>
            </div>
            
            {/* Status Badges */}
            <div className="flex gap-2 mt-4 flex-wrap">
              <Badge variant={isConnected ? 'default' : 'destructive'}>
                {isConnected ? <Wifi className="w-3 h-3 mr-1" /> : <WifiOff className="w-3 h-3 mr-1" />}
                QZ Tray {isConnected ? 'OK' : 'Offline'}
              </Badge>
              <Badge variant={printerConfig.customer ? 'default' : 'secondary'}>
                <Printer className="w-3 h-3 mr-1" />
                {printerConfig.customer || 'Kein Drucker'}
              </Badge>
              {lastCheck && (
                <Badge variant="outline">
                  <Clock className="w-3 h-3 mr-1" />
                  {lastCheck.toLocaleTimeString('de-DE')}
                </Badge>
              )}
              {isPrinting && (
                <Badge className="bg-yellow-600">
                  <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                  Druckt...
                </Badge>
              )}
            </div>
          </CardContent>
        </Card>

        {/* QZ Tray Connection */}
        {!isConnected && (
          <Card className="bg-red-900/20 border-red-500/50">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <WifiOff className="w-8 h-8 text-red-500" />
                <div className="flex-1">
                  <div className="font-medium text-red-400">QZ Tray nicht verbunden</div>
                  <div className="text-sm text-gray-400">Stellen Sie sicher, dass QZ Tray läuft</div>
                </div>
                <Button onClick={connect} disabled={isConnecting} variant="outline">
                  {isConnecting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Verbinden'}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Drucker Konfiguration */}
        <Card className="bg-gray-800 border-gray-700">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-white text-lg">Drucker</CardTitle>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => setConfigMode(!configMode)}
              >
                <Settings className="w-4 h-4" />
              </Button>
            </div>
          </CardHeader>
          {configMode && isConnected && (
            <CardContent className="space-y-3">
              <div>
                <label className="text-sm text-gray-400 mb-1 block">Kundenbon-Drucker *</label>
                <Select 
                  value={printerConfig.customer || 'none'}
                  onValueChange={(v) => savePrinterConfig('customer', v === 'none' ? '' : v)}
                >
                  <SelectTrigger className="bg-gray-700 border-gray-600">
                    <SelectValue placeholder="Drucker wählen..." />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-800 border-gray-700">
                    <SelectItem value="none">-- Nicht zugewiesen --</SelectItem>
                    {printers.map(p => (
                      <SelectItem key={p} value={p}>{p}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm text-gray-400 mb-1 block">Küchen-Drucker (optional)</label>
                <Select 
                  value={printerConfig.kitchen || 'none'}
                  onValueChange={(v) => savePrinterConfig('kitchen', v === 'none' ? '' : v)}
                >
                  <SelectTrigger className="bg-gray-700 border-gray-600">
                    <SelectValue placeholder="Drucker wählen..." />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-800 border-gray-700">
                    <SelectItem value="none">-- Nicht zugewiesen --</SelectItem>
                    {printers.map(p => (
                      <SelectItem key={p} value={p}>{p}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          )}
        </Card>

        {/* Einstellungen */}
        <Card className="bg-gray-800 border-gray-700">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {soundEnabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
                <span>Ton bei neuer Bestellung</span>
              </div>
              <Switch 
                checked={soundEnabled} 
                onCheckedChange={setSoundEnabled}
              />
            </div>
          </CardContent>
        </Card>

        {/* Letzte Drucke */}
        {recentPrints.length > 0 && (
          <Card className="bg-gray-800 border-gray-700">
            <CardHeader className="pb-2">
              <CardTitle className="text-white text-lg flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-teal-500" />
                Zuletzt gedruckt
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {recentPrints.map((print, i) => (
                  <div key={print.id + i} className="flex items-center justify-between py-2 border-b border-gray-700 last:border-0">
                    <div>
                      <span className="font-medium">{print.receipt_no}</span>
                      <span className="text-gray-400 ml-2">{print.total?.toFixed(2)} €</span>
                    </div>
                    <span className="text-sm text-gray-500">{print.time}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Anleitung */}
        <Card className="bg-gray-800/50 border-gray-700">
          <CardContent className="pt-4 text-sm text-gray-400">
            <p className="font-medium text-white mb-2">So funktioniert's:</p>
            <ol className="list-decimal list-inside space-y-1">
              <li>Diese Seite auf dem Restaurant-PC offen lassen</li>
              <li>QZ Tray muss im Hintergrund laufen</li>
              <li>Drucker oben konfigurieren</li>
              <li>"Starten" klicken</li>
              <li>Bestellungen vom Handy werden automatisch gedruckt!</li>
            </ol>
          </CardContent>
        </Card>

      </div>
    </div>
  );
}
